import { useTranslation } from 'react-i18next';

import { useVariableMechanismAllowed } from './use-variable-affordance';

import { CheckboxRow } from '@/components/ui/checkbox-row';

/**
 * THE BRANCHING-CORRECTS TOGGLE, in one place because it is gated.
 *
 * Eight blocks offer it and every one of them had written the same `CheckboxRow` by hand with
 * the same label key. That was harmless while the only condition was "the question holds a
 * variable chip, and we are in graded mode" — each block already knew both, and the duplication
 * cost nothing but lines.
 *
 * A capability changes that. The roster invariant's reasoning applies exactly: a gate on a shared
 * control is one conditional, and the same gate pushed out to eight callers is eight, one of
 * which forgets — silently, because the control still works for everyone who has the rung. So the
 * component is the gate, and the blocks keep the conditions they already owned.
 *
 * ## What it does NOT take over
 *
 * Each block still decides whether there is anything to branch on. GPS asks
 * `htmlHasVariableChips(question)`, Sort asks `hasBranchableVariable`, the six that bank a draft
 * compute `showConditionalUI`. Those are per-block facts about the block's own data and they stay
 * where they are; this adds the one question none of them can answer for itself.
 */
export function BranchingCorrectsRow({ checked, onToggle, fieldId }: {
  checked: boolean;
  onToggle: () => void;
  fieldId?: string;
}) {
  const { t } = useTranslation();
  /*
   * Grandfathered on the tick itself. A block already branching keeps its row whatever the rung
   * says, because this checkbox is the only way to stop — gate adoption, never abandonment. It
   * also means a lowered rung never changes how a block GRADES: the setup stays, visible and
   * reversible, rather than quietly ceasing to apply.
   */
  const allowed = useVariableMechanismAllowed(checked);
  if (!allowed) return null;

  return (
    <CheckboxRow
      fieldId={fieldId}
      checked={checked}
      onToggle={onToggle}
      label={t('blocks.branching.conditionalCorrectnessToggle')}
    />
  );
}
